import { Box, InputGroup, InputRightElement, VStack } from "@chakra-ui/react";
import { CalendarIcon } from "@chakra-ui/icons";
import { Instant, LocalDate } from "@js-joda/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React from "react";
import type { Messages, ResponseOf } from "../../../../../core/api";
import { TableCellBadge } from "../../../../communication/components/TableCellBadge";
import RangeDatePicker from "../../../../../shared/components/DatePicker/RangeDatePicker";
import Select from "../../../../../shared/components/Select";
import useApi from "../../../../../shared/hooks/useApi";
import useEntityProfileToast from "../../../../entityProfileModal/hooks/useEntityProfileToast";
import { FaxDetailsId, PatientId } from "../../../../../shared/schema/schema";
import { dateFormatter } from "../../../../../shared/utils/date-formatter";
import { formatErrorResponse } from "../../../../../shared/utils/format-response-error";
import { phoneFormatter } from "../../../../../shared/utils/phone-formatter";
import EntityProfileQuerySection from "../../../../entityProfileModal/components/EntityProfileQuerySection";
import EntityProfileDataTable, {
  type EntityProfileDataTableColumn,
} from "../../../../entityProfileModal/components/EntityProfileDataTable";
import EntityProfileFilterBar, {
  EntityProfileFilterBarItem,
} from "../../../../entityProfileModal/components/EntityProfileFilterBar";
import { entityProfileModalColors } from "../../../../entityProfileModal/entityProfileModal.theme";
import type { PatientProfileBridgeActions } from "../../../patientProfileModal.actions";
import EntityModalButton from "../../../../entityProfileModal/components/EntityModalButton";

type Props = {
  actions: PatientProfileBridgeActions;
  patientId: PatientId;
};

const defaultFaxStatuses: Messages["FaxStatus"][] = [
  "processing",
  "failed",
  "success",
  "transmitting",
];

const faxStatusOptions: { label: string; value: Messages["FaxStatus"] }[] = [
  { label: "PROCESSING", value: "processing" },
  { label: "FAILED", value: "failed" },
  { label: "SUCCESS", value: "success" },
  { label: "TRANSMITTING", value: "transmitting" },
];

const faxStatusLabels: Record<Messages["FaxStatus"], string> = {
  failed: "Failed",
  processing: "Processing",
  success: "Success",
  transmitting: "Transmitting",
};

const faxStatusTextToColor = {
  failed: "red" as const,
  processing: "yellow" as const,
  success: "green" as const,
  transmitting: "yellow" as const,
};

const faxPageSizes = [10, 25, 50, 100] as const;

type FaxRow = ResponseOf<"get", "./faxes">["faxes"][number];
type FaxSortKey = "createdAt";

function toEpochMillis(value: FaxRow["createdAt"]) {
  return value instanceof Instant ? value.toEpochMilli() : new Date(String(value)).getTime();
}

export default function PatientProfileFaxesSection(props: Props) {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const toast = useEntityProfileToast();
  const [selectedStatuses, setSelectedStatuses] =
    React.useState<Messages["FaxStatus"][]>(defaultFaxStatuses);
  const [updatingFaxId, setUpdatingFaxId] = React.useState<FaxDetailsId | null>(null);
  const [createdAtFrom, setCreatedAtFrom] = React.useState<LocalDate | null>(() =>
    LocalDate.now().minusDays(29)
  );
  const [createdAtTo, setCreatedAtTo] = React.useState<LocalDate | null>(() => LocalDate.now());
  const queryKey = [
    "patientProfileModal",
    props.patientId,
    "faxes",
    selectedStatuses,
    createdAtFrom,
    createdAtTo,
  ];
  const faxesQuery = useQuery({
    queryKey,
    queryFn: () =>
      api.get("./faxes", {
        query: {
          patient: props.patientId,
          faxStatus: selectedStatuses.length > 0 ? selectedStatuses : undefined,
          createdAtFrom: createdAtFrom ?? undefined,
          createdAtTo: createdAtTo ?? undefined,
        },
      }),
  });
  const updateFaxStatus = useMutation({
    mutationFn: (faxId: FaxDetailsId) => {
      setUpdatingFaxId(faxId);

      return api.post("./faxes/update_status", {
        body: { id: faxId },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({
        description: "Fax status updated.",
        status: "success",
      });
    },
    onError: (error) => {
      toast({
        title: "Could not update fax status",
        description: formatErrorResponse(error),
        status: "error",
      });
    },
    onSettled: () => setUpdatingFaxId(null),
  });
  const handleStatusChange = (statuses: Messages["FaxStatus"][] | undefined) => {
    setSelectedStatuses(statuses ?? []);
  };
  const handleCreatedAtChange = (value: [LocalDate, LocalDate | null] | null) => {
    setCreatedAtFrom(value?.[0] ?? null);
    setCreatedAtTo(value?.[1] ?? null);
  };
  const getStatusSelectLabel = (statuses: Messages["FaxStatus"][]) => {
    if (statuses.length === defaultFaxStatuses.length) {
      return "All statuses";
    }

    if (statuses.length === 0) {
      return "None";
    }

    return statuses.map((status) => faxStatusLabels[status]).join(", ");
  };
  const filters = (
    <EntityProfileFilterBar>
      <EntityProfileFilterBarItem label="Created At" maxW={{ base: "100%", md: "360px" }}>
        <InputGroup maxW="360px">
          <RangeDatePicker
            dateFormat="MM/dd/yyyy"
            endDate={createdAtTo}
            inputProps={{ h: "34px", fontSize: "14px", width: "full" }}
            selected={createdAtTo}
            startDate={createdAtFrom}
            onChange={handleCreatedAtChange}
          />
          <InputRightElement h="34px">
            <CalendarIcon color={entityProfileModalColors.muted} />
          </InputRightElement>
        </InputGroup>
      </EntityProfileFilterBarItem>
      <EntityProfileFilterBarItem label="Status" maxW={{ base: "100%", md: "220px" }}>
        <Select
          multiple
          buttonProps={{
            bg: entityProfileModalColors.surface,
            borderColor: entityProfileModalColors.inputBorder,
            borderRadius: "4px",
            color: entityProfileModalColors.title,
            fontSize: "14px",
            fontWeight: 400,
            h: "34px",
            maxW: "220px",
            minW: "220px",
          }}
          label="Select"
          options={faxStatusOptions}
          searchable={false}
          selectedLabel={getStatusSelectLabel}
          value={selectedStatuses}
          width="220px"
          onChange={handleStatusChange}
        />
      </EntityProfileFilterBarItem>
    </EntityProfileFilterBar>
  );

  const faxes = faxesQuery.data?.faxes ?? [];
  const columns = React.useMemo<Array<EntityProfileDataTableColumn<FaxRow, FaxSortKey>>>(
    () => [
      {
        key: "type",
        header: "Type",
        render: (fax) => fax.type,
      },
      {
        key: "createdAt",
        header: "Created At",
        render: (fax) => dateFormatter.toDateOrDateTime(fax.createdAt),
        sortKey: "createdAt",
        sortValue: (fax) => toEpochMillis(fax.createdAt),
      },
      {
        key: "status",
        header: "Status",
        render: (fax) => (
          <TableCellBadge
            color={faxStatusTextToColor[fax.status]}
            text={fax.status.toUpperCase()}
          />
        ),
      },
      {
        key: "createdBy",
        header: "Created By",
        render: (fax) => fax.createdByUserName ?? "",
      },
      {
        key: "sentFrom",
        header: "Sent From",
        render: (fax) => phoneFormatter.formatNationalIfValid(fax.sender),
      },
      {
        key: "sentTo",
        header: "Sent To",
        render: (fax) => phoneFormatter.formatNationalIfValid(fax.recipient),
      },
      {
        key: "actions",
        header: "",
        render: (fax) => (
          <VStack alignItems="center" spacing={1}>
            <EntityModalButton
              disabled={
                updateFaxStatus.isPending && (updatingFaxId === null || updatingFaxId !== fax.id)
              }
              isLoading={updateFaxStatus.isPending && updatingFaxId === fax.id}
              kind={fax.isActionStatusDone ? "orange" : "blue"}
              size="xs"
              style={{ width: 90 }}
              onClick={() => updateFaxStatus.mutate(fax.id)}
            >
              {fax.isActionStatusDone ? "Mark as New" : "Mark as Done"}
            </EntityModalButton>
            {fax.status === "success" ? (
              <EntityModalButton
                kind="blue"
                size="xs"
                style={{ width: 90 }}
                variant="outline"
                onClick={() => props.actions.navigateToDocument(fax.id)}
              >
                View File
              </EntityModalButton>
            ) : null}
          </VStack>
        ),
      },
    ],
    [props.actions, updateFaxStatus, updatingFaxId]
  );

  return (
    <EntityProfileQuerySection
      errorLabel="Could not load faxes."
      filters={filters}
      isError={faxesQuery.isError}
      isLoading={faxesQuery.isLoading}
      loadingLabel="Loading faxes"
      onRetry={() => faxesQuery.refetch()}
    >
      <Box minH="300px">
        <EntityProfileDataTable
          columns={columns}
          defaultPageSize={faxPageSizes[0]}
          defaultSort={{ key: "createdAt", direction: "asc" }}
          emptyLabel="No faxes."
          getRowKey={(fax) => fax.id}
          pageSizeOptions={[...faxPageSizes]}
          rows={faxes}
        />
      </Box>
    </EntityProfileQuerySection>
  );
}
